'use es6';

import shallowEqual from 'react-utils/shallowEqual';
import { CUSTOM_CSS_PARAM_NAMES } from 'layout-data-lib/constants';
import { CELL_WIDTHS_CHANGED, TREE_COUNT_CHANGED, TREE_STRUCTURE_CHANGED, USER_STYLE_CHANGED } from './constants/layoutTreeChangeConstants';
import { isImmutable } from './dataHelpers';
const addNumberTreesChangedResult = results => {
  results[TREE_COUNT_CHANGED] = true;
};
const addTreeStructureChangedResult = (results, treeNameChanged, {
  deletedNode,
  addedNode
} = {}) => {
  // Being careful to only use the bits of Set supported by IE 11, so...
  const newResult = {
    treeNamesWithChangedStruture: new Set(),
    addedNodeNames: new Set(),
    deletedNodeNames: new Set()
  };
  results[TREE_STRUCTURE_CHANGED] = results[TREE_STRUCTURE_CHANGED] || newResult;
  const {
    addedNodeNames,
    deletedNodeNames
  } = results[TREE_STRUCTURE_CHANGED];
  if (deletedNode) {
    const nodeAlreadyAddedElsewhere = addedNodeNames.has(deletedNode.getName());
    if (nodeAlreadyAddedElsewhere) {
      // Prevent nodes moved from one tree to another from being in the addedNodeNames list
      addedNodeNames.delete(deletedNode.getName());
    } else {
      deletedNodeNames.add(deletedNode.getName());
    }
  }
  if (addedNode) {
    const nodeAlreadyDeletedElsewhere = deletedNodeNames.has(addedNode.getName());
    if (nodeAlreadyDeletedElsewhere) {
      // Prevent nodes moved from one tree to another from being in the deletedNodeNames list
      deletedNodeNames.delete(addedNode.getName());
    } else {
      addedNodeNames.add(addedNode.getName());
    }
  }
  results[TREE_STRUCTURE_CHANGED].treeNamesWithChangedStruture.add(treeNameChanged);
};
const addUserStyleChangedResult = (results, previousNode, currentNode) => {
  const newResult = {
    nodesWithChangedStyles: []
  };
  results[USER_STYLE_CHANGED] = results[USER_STYLE_CHANGED] || newResult;
  results[USER_STYLE_CHANGED].nodesWithChangedStyles.push({
    previousNode,
    currentNode
  });
};
const addCellWidthsChangedResult = (results, cellWithChangedWidth) => {
  const newResult = {
    cellsWithChangedWidth: []
  };
  results[CELL_WIDTHS_CHANGED] = results[CELL_WIDTHS_CHANGED] || newResult;
  results[CELL_WIDTHS_CHANGED].cellsWithChangedWidth.push(cellWithChangedWidth);
};
const didTreesChange = (prevTree, nextTree, results) => {
  // Being careful to only use the bits of Set supported by IE 11, so...
  const nodesNamesInPreviousTree = new Set();

  // Iterate over every node in the previous tree, comparing to that node (if it exists) in the next tree
  prevTree.allCellsAndRows({
    includeStart: true
  }).forEach(previousNode => {
    let nextNode;
    let previousChildrenNames;
    let nextChildrenNames;
    nodesNamesInPreviousTree.add(previousNode.getName());
    if (previousNode.isRow()) {
      previousChildrenNames = previousNode.getColumnNames();
      nextNode = nextTree.hasRow(previousNode.getName()) && nextTree.findRow(previousNode.getName());
      nextChildrenNames = nextNode && nextNode.getColumnNames();
      if (nextNode) {
        const previousNodeValue = previousNode.getValue() || {};
        const previousCssClass = previousNodeValue.cssClass;
        const nextNodeValue = nextNode.getValue() || {};
        const nextCssClass = nextNodeValue.cssClass;
        if (previousCssClass !== nextCssClass) {
          addUserStyleChangedResult(results, previousNode, nextNode);
        }
      }
    } else {
      previousChildrenNames = previousNode.getRowNames();
      nextNode = nextTree.hasCell(previousNode.getName()) && nextTree.findCell(previousNode.getName());
      nextChildrenNames = nextNode && nextNode.getRowNames();
      if (nextNode) {
        // Check for CSS param changes (only cells for now)
        const previousParams = previousNode.getParams();
        const nextParams = nextNode.getParams();
        const styleKeys = CUSTOM_CSS_PARAM_NAMES;
        if (previousParams && nextParams) {
          for (let x = 0; x < styleKeys.length; x++) {
            const styleKey = styleKeys[x];
            if (previousParams[styleKey] !== nextParams[styleKey]) {
              addUserStyleChangedResult(results, previousNode, nextNode);
              break;
            }
          }
        }

        // Check for width changes
        if (previousNode.getWidth() !== nextNode.getWidth()) {
          addCellWidthsChangedResult(results, nextNode);
        }
      }
    }

    // This node is missing in the next tree
    if (!nextNode) {
      addTreeStructureChangedResult(results, previousNode.getRootName(), {
        deletedNode: previousNode
      });
    }
    // Differing children (for both rows and columns)
    else if (!shallowEqual(previousChildrenNames, nextChildrenNames)) {
      addTreeStructureChangedResult(results, nextNode.getRootName());
    }
  });

  // Find all of the new nodes just added to the next tree (if an)
  nextTree.allCellsAndRows().forEach(nextNode => {
    if (!nodesNamesInPreviousTree.has(nextNode.getName())) {
      addTreeStructureChangedResult(results, nextNode.getRootName(), {
        addedNode: nextNode
      });
    }
  });
};
export const didLayoutUpdate = (prevTrees, nextTrees) => {
  const results = {};
  if (prevTrees !== nextTrees) {
    const isPrevTreeNamesImmutable = isImmutable(prevTrees);
    const prevTreeNames = isPrevTreeNamesImmutable ? prevTrees.keySeq().toArray() : Object.keys(prevTrees);
    const isNextTreeNamesImmutable = isImmutable(nextTrees);
    const nextTreeNames = isNextTreeNamesImmutable ? nextTrees.keySeq().toArray() : Object.keys(nextTrees);
    if (prevTreeNames.length !== nextTreeNames.length) {
      addNumberTreesChangedResult(results);
      return results;
    }
    for (let i = 0; i < prevTreeNames.length; i++) {
      const prevTree = isPrevTreeNamesImmutable ? prevTrees.get(prevTreeNames[i]) : prevTrees[prevTreeNames[i]];
      const nextTree = isNextTreeNamesImmutable ? nextTrees.get(nextTreeNames[i]) : nextTrees[nextTreeNames[i]];
      if (prevTree === nextTree) {
        continue;
      }
      if (!nextTree) {
        addTreeStructureChangedResult(results, nextTree.getName());
        continue;
      }
      didTreesChange(prevTree, nextTree, results);
    }
  }
  return results;
};